import { AccountInfoData, LspModuleData } from 'types';

const normalizeName = (value?: string) => (value || '').trim().toLowerCase();

const isActiveModule = (module?: LspModuleData) => {
  if (!module || module.isActive === false) {
    return false;
  }

  if (module.endsAt) {
    const endsAt = new Date(module.endsAt);
    if (!Number.isNaN(endsAt.getTime()) && endsAt.getTime() <= Date.now()) {
      return false;
    }
  }

  return true;
};

const hasActiveModule = (accountInfo: AccountInfoData | undefined, name: string) =>
  (accountInfo?.allowedModules ?? []).some(
    (module) => normalizeName(module.name) === name && isActiveModule(module),
  );

const livestockModuleNames = ['diko', 'slut'];

const hasActiveLivestockModule = (accountInfo?: AccountInfoData) =>
  (accountInfo?.allowedModules ?? []).some(
    (module) =>
      livestockModuleNames.includes(normalizeName(module.name)) && isActiveModule(module),
  );

/** Full växtodling (`vaxt`) — same signal as app selection `hasActiveCropsModule`. */
export const hasActiveVaxtModule = (accountInfo?: AccountInfoData) =>
  hasActiveModule(accountInfo, 'vaxt');

/**
 * Free or standalone digital spray journal (`kemi`) when full `vaxt` is not active
 * (matches app selection hiding the standalone kemi card when `vaxt` is on).
 */
export const hasStandaloneChemicalJournalAccess = (accountInfo?: AccountInfoData) => {
  if (hasActiveVaxtModule(accountInfo)) {
    return false;
  }

  return hasActiveModule(accountInfo, 'kemi');
};

/** Farm-scoped entitlements from auth account-info (aligned with app selection). */
export const hasLivestockProductAccess = (accountInfo?: AccountInfoData) => {
  if (hasActiveLivestockModule(accountInfo)) {
    return true;
  }
  return accountInfo?.hasLspAccount === true;
};

export const hasBoardProductAccess = (accountInfo?: AccountInfoData) =>
  accountInfo?.hasBoardAccount === true || hasActiveModule(accountInfo, 'tavla');

/** Active full crops app — not the free journal alone. */
export const hasCropsProductAccess = (accountInfo?: AccountInfoData) =>
  hasActiveVaxtModule(accountInfo);

export const countFarmProducts = (accountInfo?: AccountInfoData) =>
  Number(hasBoardProductAccess(accountInfo))
  + Number(hasCropsProductAccess(accountInfo))
  + Number(hasStandaloneChemicalJournalAccess(accountInfo))
  + Number(hasLivestockProductAccess(accountInfo));
